// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { useCurrentUser } from '@/authentication/hooks/use-current-user';
import { GlobalSearch } from '@/components/global-search';
import { NavigationDropdown } from '@/components/navigation-dropdown';
import { NavigationSubsectionBar } from '@/components/navigation-subsection-bar';
import { PlanStatusPill } from '@/components/plan-status-pill';
import { UserDropdownMenu } from '@/components/user-dropdown-menu';
import { MenuRounded } from '@/constants/hardfin-standard-icons';
import { navigationSectionsConfig } from '@/constants/navigation-sections';
import { useNavigationContext } from '@/contexts/navigation-context';
import { getFileImageURLForFileKey } from '@/file/utilities/get-download-url';
import { useDisclosure } from '@/hooks/use-disclosure';
import { NotificationBell } from '@/notification/components/notification-bell';
import {
  AppBar,
  Avatar,
  Box,
  Chip,
  Container,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Stack,
  Typography,
  styled,
  useTheme,
} from '@mui/material';
import { Link } from '@tanstack/react-router';
import { createRef, useEffect, useRef, useState } from 'react';

const IconAvatar = styled(Avatar)((props) => ({
  backgroundColor: props.src === undefined ? undefined : 'transparent',
  '& > img': {
    objectFit: 'contain',
  },
}));

export function NavigationBar() {
  const theme = useTheme();
  const mobileMenu = useDisclosure();
  const appBarRef = useRef<HTMLDivElement>(null);
  const [appBarHeight, setAppBarHeight] = useState<number>(56);

  // Create refs for navigation sections to measure their positions
  const navigationSectionRefs = useRef<React.RefObject<HTMLDivElement>[]>([]);

  const { user } = useCurrentUser();
  const { activeSection, activeSubsection, setActiveSectionAndSubsection } =
    useNavigationContext();
  const env = import.meta.env.VITE_ENVIRONMENT_NAME;

  // Define special navbar background color for sandbox environments
  const environmentColor =
    env === 'sandbox'
      ? theme.palette.warning.light
      : theme.palette.background.paper;

  // Compute navigation sections with dynamic isActive property based on context
  const navigationSections = navigationSectionsConfig.map((section) => ({
    ...section,
    isActive: activeSection === section.label,
  }));

  // Initialize refs for each navigation section
  if (navigationSectionRefs.current.length !== navigationSections.length) {
    navigationSectionRefs.current = navigationSections.map(() =>
      createRef<HTMLDivElement>()
    );
  }

  // Find the active section ref
  const activeSectionIndex = navigationSections.findIndex(
    (section) => section.isActive
  );
  const activeSectionRef =
    activeSectionIndex !== -1
      ? navigationSectionRefs.current[activeSectionIndex]
      : undefined;

  // Measure actual AppBar height for drawer positioning
  useEffect(() => {
    const measureAppBarHeight = () => {
      if (appBarRef.current) {
        const height = appBarRef.current.offsetHeight;
        setAppBarHeight(height);
      }
    };

    measureAppBarHeight();
    window.addEventListener('resize', measureAppBarHeight);

    return () => {
      window.removeEventListener('resize', measureAppBarHeight);
    };
  }, []);

  return (
    <AppBar
      ref={appBarRef}
      position="sticky"
      color="inherit"
      elevation={0}
      sx={{
        borderBottom: '1px solid',
        borderBottomColor: 'divider',
        backgroundColor: environmentColor,
      }}
    >
      <Container maxWidth={false}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={2}
            paddingRight={2}
          >
            <IconButton
              onClick={mobileMenu.open}
              sx={{ display: { xs: 'block', sm: 'none' } }}
            >
              <MenuRounded />
            </IconButton>
            <Stack
              component={Link}
              to="/dashboard/asset"
              onClick={() =>
                setActiveSectionAndSubsection('Dashboards', 'Assets')
              }
              direction="row"
              alignItems="center"
              spacing={1.5}
              sx={{ textDecoration: 'none' }}
            >
              <IconAvatar
                src={getFileImageURLForFileKey(user.organization.iconFileKey)}
              >
                {user.organization.name.at(0)}
              </IconAvatar>
              <Typography variant="body2" color="text.primary">
                {user.organization.name}
              </Typography>
            </Stack>
          </Stack>
          <Stack
            component="nav"
            direction="row"
            alignSelf="stretch"
            marginRight="auto"
            sx={{ display: { xs: 'none', sm: 'flex' } }}
          >
            {navigationSections.map((section, index) => (
              <NavigationDropdown
                key={section.label}
                ref={navigationSectionRefs.current[index]}
                label={section.label}
                icon={section.icon}
                items={section.items}
                isActive={section.isActive}
                activeSubsection={activeSubsection}
                onSubsectionClick={(subsectionLabel) =>
                  setActiveSectionAndSubsection(section.label, subsectionLabel)
                }
              />
            ))}
          </Stack>

          <Stack direction="row" alignItems="center" spacing={1}>
            <GlobalSearch />
            <NotificationBell />
            <UserDropdownMenu />
          </Stack>
          <PlanStatusPill plan={user.organization.plan} />
        </Stack>
      </Container>

      {/* Subsection navigation bar */}
      <NavigationSubsectionBar
        items={(() => {
          const activeSection = navigationSections.find(
            (section) => section.isActive
          );
          if (activeSection && activeSection.items) {
            return activeSection.items;
          }
          return [];
        })()}
        activeSectionRef={activeSectionRef}
        alignment={(() => {
          const activeSection = navigationSections.find(
            (section) => section.isActive
          );

          if (activeSection) {
            return (
              (activeSection.subsectionAlignment as
                | 'left'
                | 'center'
                | 'right') || 'left'
            );
          }
          return 'left';
        })()}
      />

      <Divider />

      {/* Mobile menu drawer */}
      <Drawer
        anchor="left"
        open={mobileMenu.isOpen}
        onClose={mobileMenu.close}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': {
            top: `${appBarHeight}px`,
            height: `calc(100vh - ${appBarHeight}px)`,
          },
        }}
      >
        <Box sx={{ width: 280 }}>
          <List>
            {navigationSections.map((section) => (
              <div key={section.label}>
                <ListSubheader
                  sx={{
                    fontSize: '1rem',
                    fontWeight: 600,
                    lineHeight: 1,
                    paddingTop: 2,
                    paddingBottom: 1,
                    background: `linear-gradient(to bottom, ${theme.palette.background.paper} 0%, ${theme.palette.background.paper} 70%, transparent 100%)`,
                  }}
                >
                  {section.label}
                </ListSubheader>
                {section.items.map((item) => {
                  // Highlight subsection if it matches the active subsection
                  const isActive =
                    section.isActive && activeSubsection === item.label;
                  const isDisabled = item.disabled || false;

                  return (
                    <ListItem key={item.to} disablePadding>
                      <ListItemButton
                        component={isDisabled ? 'div' : Link}
                        to={isDisabled ? undefined : item.to}
                        onClick={
                          isDisabled
                            ? undefined
                            : () => {
                                setActiveSectionAndSubsection(
                                  section.label,
                                  item.label
                                );
                                mobileMenu.close();
                              }
                        }
                        disabled={isDisabled}
                        sx={{
                          paddingY: 0.5,
                          paddingLeft: 3,
                          cursor: isDisabled ? 'default' : 'pointer',
                          backgroundColor:
                            isActive && !isDisabled
                              ? theme.palette.primary.light + '20'
                              : 'transparent',
                          '&:hover': isDisabled ? {} : undefined,
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            color: isDisabled
                              ? theme.palette.text.disabled
                              : isActive
                                ? theme.palette.primary.main
                                : 'text.secondary',
                          }}
                        >
                          {item.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                              }}
                            >
                              <Typography
                                variant="body2"
                                fontWeight={isActive && !isDisabled ? 600 : 400}
                                color={
                                  isDisabled
                                    ? 'text.disabled'
                                    : isActive
                                      ? theme.palette.primary.main
                                      : 'text.primary'
                                }
                              >
                                {item.label}
                              </Typography>
                              {item.dropdownChip && (
                                <Chip
                                  label={item.dropdownChip}
                                  size="small"
                                  variant="outlined"
                                  sx={{
                                    height: '18px',
                                    fontSize: '0.625rem',
                                    fontWeight: 500,
                                    color: theme.palette.text.secondary,
                                    borderColor: theme.palette.divider,
                                    backgroundColor: 'transparent',
                                  }}
                                />
                              )}
                            </Box>
                          }
                          secondary={item.description}
                          secondaryTypographyProps={{
                            variant: 'caption',
                            sx: {
                              fontSize: '0.7rem',
                              color: isDisabled
                                ? 'text.disabled'
                                : 'text.secondary',
                            },
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </div>
            ))}
          </List>
        </Box>
      </Drawer>
    </AppBar>
  );
}
